/* eslint-disable react/prop-types */

import React, { useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  LabelList,
  PieChart,
  Pie,
  Cell,
  Sector,
  SectorProps
} from 'recharts';
import { TrendingUp, ArrowRight } from 'lucide-react';
import {
  formatCurrencyAmount,
  formatNumberValue,
  formatPercentageValue,
  getFormattedSentence,
  getVehicleProtectionFromKey,
  removeUnderscoresAndCapitalize,
  sentenceCase
} from '../../../../../utils/miscFunctions';
import DashboardTabs from '../../../../../components/dashboard/tabs/DashboardTabs';
import DoughnutChart from '../../../../../components/dashboard/charts/DonutChart';
import { SalesMixDonutsBgColors } from '../../../../../resources/constants';
import { EmptyState } from '../../../../shared/dashboard/broker/components/SalesTrendsChart';

interface PaymentHistoryChartProps {
  labels: string[];
  policyCount: number[];
  premium: number[];
  productData?: any;
  trendData?: any;
  metricsSummary?: any;
  loading?: boolean;
  chartOption1: string;
  chartOption2: string;
  teamMetrics?: any;
  
}

interface ActiveShapeProps extends SectorProps {
  outerRadius: number;
  cx?: number;
  cy?: number;
  innerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}
interface IChartItem {
  count?: number;
  label?: string;
  percentage?: number;
  premium?: number;
  color?: string;
}

interface ChartData {
  name: string;
  RevenueData: number;
  VolumeData: number;
}


type PayloadItem = {
 
    name: string;
    value: number;
    [key: string]: any;
  
};


interface CustomTooltipProps {
  active?: boolean;
  label?: string;
  payload?: PayloadItem[];
  name?: string;
}

const PaymentHistoryChart: React.FC<PaymentHistoryChartProps> = ({
  labels,
  policyCount,
  premium,
  teamMetrics,
  metricsSummary,
  loading,
  chartOption1,
  chartOption2
}) => {
  // Format the data for Recharts
  const trendChartData: ChartData[] = [
    {
      name: 'Jan',
      RevenueData: teamMetrics['mom']?.sales?.JANUARY,
      VolumeData: teamMetrics['mom']?.count?.JANUARY
    },
    {
      name: 'Feb',
      RevenueData: teamMetrics['mom']?.sales?.FEBRUARY,
      VolumeData: teamMetrics['mom']?.count?.FEBRUARY
    },
    {
      name: 'Mar',
      RevenueData: teamMetrics['mom']?.sales?.MARCH,
      VolumeData: teamMetrics['mom']?.count?.MARCH
    },
    {
      name: 'Apr',
      RevenueData: teamMetrics['mom']?.sales?.APRIL,
      VolumeData: teamMetrics['mom']?.count?.APRIL
    },
    {
      name: 'May',
      RevenueData: teamMetrics['mom']?.sales?.MAY,
      VolumeData: teamMetrics['mom']?.count?.MAY
    },
    {
      name: 'Jun',
      RevenueData: teamMetrics['mom']?.sales?.JUNE,
      VolumeData: teamMetrics['mom']?.count?.JUNE
    },
    {
      name: 'Jul',
      RevenueData: teamMetrics['mom']?.sales?.JULY,
      VolumeData: teamMetrics['mom']?.count?.JULY
    },
    {
      name: 'Aug',
      RevenueData: teamMetrics['mom']?.sales?.AUGUST,
      VolumeData: teamMetrics['mom']?.count?.AUGUST
    },
    {
      name: 'Sept',
      RevenueData: teamMetrics['mom']?.sales?.SEPTEMBER,
      VolumeData: teamMetrics['mom']?.count?.SEPTEMBER
    },
    {
      name: 'Oct',
      RevenueData: teamMetrics['mom']?.sales?.OCTOBER,
      VolumeData: teamMetrics['mom']?.count?.OCTOBER
    },
    {
      name: 'Nov',
      RevenueData: teamMetrics['mom']?.sales?.NOVEMBER,
      VolumeData: teamMetrics['mom']?.count?.NOVEMBER
    },
    {
      name: 'Dec',
      RevenueData: teamMetrics['mom']?.sales?.DECEMBER,
      VolumeData: teamMetrics['mom']?.count?.DECEMBER
    }
  ];

  // Pie chart data

  // const metricsPremiumData: Record<string, number> = {};
  // const metricsPolicyCountData: Record<string, number> = {};

  // labels.forEach((label: string, index: number) => {
  //   metricsPremiumData[getVehicleProtectionFromKey(label)?.title ?? ''] = premium[index];
  // });

  // labels.forEach((label: string, index: number) => {
  //   metricsPolicyCountData[getVehicleProtectionFromKey(label)?.title ?? ''] = policyCount[index];
  // });

  // State for active payment type in the pie chart
  const [activePaymentIndex, setActivePaymentIndex] = useState<number>(0);
  const dataNotAvailableMessage = (title: string) =>{
    return (
          <div className="bg-white min-w-[200px]">
            <EmptyState
              icon="📊"
              title={`No ${title} data available`}
              description={`There is no ${title} data to display.`}
              
            />
          </div>
    )
  }
 
  const CustomTooltip2: React.FC<CustomTooltipProps> = ({ active, payload, name }) => {
    if (!active || !payload || !payload.length) return null;
   
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md text-sm">
        <p className="font-medium text-gray-900">{getFormattedSentence(data.label)}</p>
        {name == 'Amount' && <p className="text-gray-600">Premium: {formatCurrencyAmount(data.premium)}</p>}
         <p className="text-gray-600">Policy count: {data.count}</p>
      </div>
    );
  };

   const chartCountData: IChartItem[] = labels.map((label, index) => ({
    label,
    count: policyCount[index], 
    color: SalesMixDonutsBgColors[index]
  }));

   const chartPremiumData: IChartItem[] = labels.map((label, index) => ({
    label,
    premium: premium[index], 
    count: policyCount[index],
    color: SalesMixDonutsBgColors[index]
  }));

  const renderPieChart = (data:IChartItem[], name: string) => {
    const ActiveShape = ({
      cx = 0,
      cy = 0,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill
    }: ActiveShapeProps) => (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 8}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
    const [activeIndex, setActiveIndex] = useState<Record<string, number | null>>({
      policy: null,
      insurer: null
    });

 if (!loading && !(chartCountData.length || chartPremiumData.length)) {
    return (
     <>{dataNotAvailableMessage('product')}</>
    );
  }

    return (
      <PieChart>
        <Pie
          //activeIndex={undefined}
          activeShape={ActiveShape as any}
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          dataKey={name == 'Amount' ? 'premium' : 'count'}
          nameKey="label"
          onMouseEnter={(_, index) => setActiveIndex((prev) => ({ ...prev }))}
          onMouseLeave={() => setActiveIndex((prev) => ({ ...prev }))}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip2 name={name}/>} />
        <Legend
          layout="horizontal"
          verticalAlign="bottom"
          align="center"
          formatter={(value) => <span className="text-xs text-gray-700 dark:text-darkBg-mainText">{getFormattedSentence(value)}</span>}
        />
      </PieChart>
    );
  };

  // Custom tooltip for the chart
 const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 bg-white shadow-lg rounded-md border border-gray-100">
        <p className="font-medium text-sm text-gray-900">{label}</p>
        <div>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} className="text-sm" style={{ color: entry.color }}>
              {entry.name}:{' '}
              {entry.name === 'Volume' ? entry.value : formatCurrencyAmount(entry.value)}
            </p>
          ))}
        </div>
      </div>
    );
  }
  return null;
};
  interface RenderActiveShapeProps {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
    payload: {
      name: string;
      value: number;
      color: string;
    };
    value: number;
  }

  // Toggle active pie sector
  const onPieEnter = (_: any, index: number) => {
    setActivePaymentIndex(index);
  };

  // Chart type tabs
  const [chartType, setChartType] = useState<'composed' | 'bars' | 'area'>('bars');

  let monthlyAverageTotal = 0;
  let monthlyAverageAmount = 0;
  let monthlyTotalCount = 0;

  for (const month in metricsSummary?.monthlyAverageLoan) {
    monthlyAverageTotal += metricsSummary?.monthlyAverageLoan[month].totalAmount;
    monthlyAverageAmount += metricsSummary?.monthlyAverageLoan[month].averageAmount;
    monthlyTotalCount += metricsSummary?.monthlyAverageLoan[month].count;
  }

  return (
    <div className="bg-white shadow  rounded-lg p-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
        <div className="lg:col-span-2  ">
          {/* Chart Type Tabs */}
          <div className="flex items-center gap-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Monthly Performance</h3>
            <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-full">
              <button
                className={`px-3 py-1 text-sm font-medium rounded-full ${
                  chartType === 'bars' ? 'bg-[#FBC02D] shadow' : ''
                }`}
                onClick={() => setChartType('bars')}>
                Comparison
              </button>
              <button
                className={`px-3 py-1 text-sm font-medium rounded-full ${
                  chartType === 'area' ? 'bg-[#FBC02D] shadow' : ''
                }`}
                onClick={() => setChartType('area')}>
                Trends
              </button>
            </div>
          </div>

          {/* Chart */}
          <div className="h-60 md:h-80 lg:h-full lg:max-h-96">
            <ResponsiveContainer width="100%" height="100%">
              {(() => {
                switch (chartType) {
                  case 'composed':
                    return (
                      <ComposedChart
                        data={trendChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20
                        }}
                        className="text-xs"
                        layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => value} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="RevenueData"
                          fill="#ef4444"
                          name={`${chartOption1}`}
                          barSize={20}
                        />
                        <Bar
                          dataKey="VolumeData"
                          fill="#3b82f6"
                          name={`${chartOption2}`}
                          barSize={20}
                        />
                      </ComposedChart>
                    );
                  case 'bars':
                    return (
                      !teamMetrics['mom']
                        ?
                          <>{dataNotAvailableMessage('monthly performance')}</>
                        :
                        <ComposedChart
                          data={trendChartData}
                          margin={{
                            top: 20,
                            right: 60,
                            bottom: 20,
                            left: 20
                          }}
                          className="text-xs">
                          <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                          <XAxis
                            dataKey="name"
                            tickFormatter={(value) => sentenceCase(value.substring(0, 3))}
                          />
                          <YAxis
                            yAxisId="left"
                            tickFormatter={(value: number) => formatCurrencyAmount(value)}
                            orientation="left"
                          />
                          <YAxis
                            yAxisId="right"
                            tickFormatter={(value: number) => formatNumberValue(value).toString()}
                            orientation="right"
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="RevenueData"
                            fill="#3b82f6"
                            name={`${chartOption1}`}
                          />
                          <Bar
                            yAxisId="right"
                            dataKey="VolumeData"
                            fill="#ef4444"
                            name={`${chartOption2}`}
                          />
                        </ComposedChart>
                    );
                  case 'area':
                    return (
                      !teamMetrics['mom'] 
                        ?
                          <>{dataNotAvailableMessage('monthly performance')}</>
                        :<ComposedChart
                        data={trendChartData}
                        margin={{
                          top: 20,
                          right: 60,
                          bottom: 20,
                          left: 20
                        }}
                        className="text-xs">
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis
                          dataKey="name"
                          tickFormatter={(value) => sentenceCase(value.substring(0, 3))}
                        />
                        <YAxis
                          yAxisId="left"
                          tickFormatter={(value: number) => formatCurrencyAmount(value)}
                          orientation="left"
                        />
                        <YAxis
                          yAxisId="right"
                          tickFormatter={(value: number) => formatNumberValue(value).toString()}
                          orientation="right"
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey="RevenueData"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.3}
                          name={`${chartOption1}`}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                        <Area
                          yAxisId="right"
                          type="monotone"
                          dataKey="VolumeData"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.3}
                          name={`${chartOption2}`}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                      </ComposedChart>
                    );
                  default:
                    // Fallback to composed chart as default
                    return (
                      <ComposedChart
                        data={trendChartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20
                        }}
                        className="text-xs">
                        <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
                        <XAxis
                          dataKey="name"
                          tickFormatter={(value) => sentenceCase(value.substring(0, 3))}
                        />
                        <YAxis tickFormatter={(value) => formatCurrencyAmount(value)} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="RevenueData"
                          fill="#3b82f6"
                          name="Disbursements"
                          barSize={20}
                        />
                        <Bar dataKey="VolumeData" fill="#ef4444" name="Loans" barSize={20} />
                      </ComposedChart>
                    );
                }
              })()}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Stats - Takes 1 column */}
        <div className="lg:col-span-1">
          <div className="h-80 mb-6">
            <DashboardTabs
              tabGroupTitle="Products"
              titleClassName="text-lg font-medium text-gray-900"
              tab1={{
                name: 'Amount',
                children: (
                   //<DoughnutChart chartKey={'product_amount'} metricsData={metricsPremiumData} displayAsCurrency={true}  bgColors={SalesMixDonutsBgColors} cutOut={80}/>
                  <>
                    {' '}
                    <ResponsiveContainer width="100%" height="100%">
                      {renderPieChart(chartPremiumData,'Amount')}
                    </ResponsiveContainer>
                  </>
                )
              }}
              tab2={{
                name: 'Volume',
                children: (
                  // <DoughnutChart chartKey={'product_count'} metricsData={metricsPolicyCountData} bgColors={SalesMixDonutsBgColors} cutOut={80}/>
                  <>
                    {' '}
                    <ResponsiveContainer width="100%" height="100%">
                      {renderPieChart(chartCountData,'Volume')}
                    </ResponsiveContainer>
                  </>
                )
              }}
              activeTab={'Volume'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryChart;
